/**
 * <cookie-banner> (sections/cookie-banner.liquid): the cookie choice, written with Shopify's
 * Customer Privacy API so the theme (assets/analytics.js), the checkout pixel and Shopify all honour
 * one choice. Shopify decides where a banner is needed (shouldShowBanner) and where visitors may opt
 * out of the sale of their data (saleOfDataRegion); the footer's Cookie settings and Do not sell
 * buttons reopen it on every page.
 *
 * The banner view is a non-modal <dialog> along the bottom edge, so the page stays usable. The
 * settings view reopens the same dialog as a modal, so the browser keeps focus inside it.
 */
import { defineElement } from '@wispaire/global';

const CATEGORIES = ['analytics', 'marketing', 'preferences'];

/** True when Shopify asks for a banner here and the visitor has not made a choice yet. */
export function needsBanner(privacy) {
  if (!privacy.shouldShowBanner()) return false;
  const stored = privacy.currentVisitorConsent();
  return CATEGORIES.every((key) => !stored[key]);
}

/**
 * The consent a button writes. Accept and Reject set every category; Save takes the toggles, and
 * outside sale-of-data regions (where that toggle is hidden) sale_of_data follows marketing.
 */
function consentFor(action, toggles, saleRegion) {
  if (action !== 'save') {
    const allowed = action === 'accept';
    return { analytics: allowed, marketing: allowed, preferences: allowed, sale_of_data: allowed };
  }
  const [analytics, marketing, preferences] = CATEGORIES.map((name) => Boolean(toggles[name]?.checked));
  const saleOfData = saleRegion ? Boolean(toggles.sale_of_data?.checked) : marketing;
  return { analytics, marketing, preferences, sale_of_data: saleOfData };
}

/**
 * Shows Do not sell where Shopify says it applies and lets the footer buttons (re)open the banner.
 * Listens on the document, so footer markup re-rendered in the theme editor still works.
 */
export function bindFooter(doc, banner, privacy, signal) {
  if (privacy.saleOfDataRegion()) {
    for (const button of doc.querySelectorAll('[data-do-not-sell]')) button.hidden = false;
  }
  doc.addEventListener('click', (event) => {
    const trigger = event.target.closest?.('[data-cookie-settings], [data-do-not-sell]');
    if (!trigger) return;
    event.preventDefault();
    banner.open(trigger.hasAttribute('data-do-not-sell') ? 'settings' : 'banner', trigger);
  }, { signal });
}

const Base = globalThis.HTMLElement ?? class {};

export class CookieBanner extends Base {
  connectedCallback() {
    // The theme editor re-renders the section; the old element's document listener goes with it.
    this.listening = new AbortController();
    this.dialog = this.querySelector('dialog');
    this.form = this.querySelector('form');
    this.views = [...this.querySelectorAll('[data-view]')];
    this.error = this.querySelector('[data-cookie-error]');

    this.addEventListener('click', (event) => {
      const button = event.target.closest('button');
      if (button?.dataset.consent) this.choose(button.dataset.consent);
      else if (button?.dataset.cookieView) this.open(button.dataset.cookieView);
    });
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.choose('save');
    });
    // A modal fires cancel on Esc; the non-modal banner view only sees the key.
    this.dialog.addEventListener('cancel', (event) => this.cancel(event));
    this.dialog.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.view === 'banner') this.cancel(event);
    });
    this.dialog.addEventListener('close', () => {
      if (this.dialog.open) return; // switching views, not leaving
      this.trigger?.focus?.();
      this.trigger = null;
    });

    const shopify = globalThis.Shopify;
    shopify?.loadFeatures?.([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      if (error) return;
      this.privacy = shopify.customerPrivacy;
      this.toggleAttribute('data-sale-region', this.privacy.saleOfDataRegion());
      bindFooter(document, this, this.privacy, this.listening.signal);
      if (needsBanner(this.privacy) || shopify.designMode) this.open('banner');
    });
  }

  disconnectedCallback() {
    this.listening.abort();
  }

  /** Opens the banner or settings view; focus goes back to `trigger` when it closes. */
  open(view, trigger) {
    if (trigger) this.trigger = trigger;
    this.view = view;
    if (view === 'settings' && this.privacy) this.syncToggles();
    for (const element of this.views) element.hidden = element.dataset.view !== view;
    if (this.error) this.error.hidden = true;

    if (this.dialog.open) this.dialog.close();
    if (view === 'settings') this.dialog.showModal();
    else this.dialog.show();
    (this.views.find((element) => !element.hidden)?.querySelector('[data-focus]') ?? this.dialog).focus?.();
  }

  /** Esc in settings steps back to the banner; with a choice stored, Esc in the banner closes it. */
  cancel(event) {
    event.preventDefault();
    if (this.view === 'settings') this.open('banner');
    else if (!needsBanner(this.privacy)) this.dialog.close();
  }

  /**
   * Ticks each toggle to the stored choice; with nothing stored, to what is allowed right now, so
   * a toggle never looks off while the data still flows (consent is granted by default outside
   * the banner regions).
   */
  syncToggles() {
    const stored = this.privacy.currentVisitorConsent();
    const allowed = {
      analytics: () => this.privacy.analyticsProcessingAllowed(),
      marketing: () => this.privacy.marketingAllowed(),
      preferences: () => this.privacy.preferencesProcessingAllowed(),
      sale_of_data: () => this.privacy.saleOfDataAllowed(),
    };
    for (const [name, isAllowed] of Object.entries(allowed)) {
      const toggle = this.form.elements[name];
      if (toggle) toggle.checked = stored[name] ? stored[name] === 'yes' : isAllowed();
    }
  }

  /** Writes the choice for 'accept', 'reject' or 'save', then closes; a failed write says so and stays. */
  choose(action) {
    const consent = consentFor(action, this.form.elements, this.privacy.saleOfDataRegion());
    return new Promise((resolve) => {
      this.privacy.setTrackingConsent(consent, (response) => {
        if (response?.error) {
          if (this.error) this.error.hidden = false;
        } else {
          this.dialog.close();
        }
        resolve();
      });
    });
  }
}

defineElement('cookie-banner', CookieBanner);
